import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { listenSocket, unlistenSocket } from '../../../../../../utils/globals/socket'
import { TwinBigCalendarEvents } from '../../../../../../baseComponents/TwinBigCalendar/types'
import { getConfigParam } from '../../../../../../utils/reducers/getters'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'
import { TaskModelType } from '@teinor/erp/types/company/task'
import TwinBigCalendar from '../../../../../../baseComponents/TwinBigCalendar'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import CustomToolbarYears from '../../../../../../baseComponents/TwinBigCalendar/CustomToolbarYears'

interface CustomerTaskCalendarDashboardProps extends CustomerTaskCalendarDashboardLogicProps { 
    title: string
}

const CustomerTaskCalendarDashboard: React.FC<CustomerTaskCalendarDashboardProps> = ({ allRowData, title }) => {
    const { getTaskCalendarData, setYear, year, events, handleOnNavigate } = useCustomerTaskCalendarDashboardLogic({ allRowData })
    return (
        <div className='flex flex-auto flex-col tce_employee_holidays_calendar'>
            <div className='flex items-center mb-15 justify-between'>
                <h3>{title}</h3>
                <CustomToolbarYears getData={getTaskCalendarData} setYear={setYear} year={year} />
            </div>
            <TwinBigCalendar date={new Date(`${year}-01-01`)} events={events || []} defaultView='year' onNavigate={handleOnNavigate} className='tce_employee_holidays_calendar_wrapper' />
        </div>
    )
}

interface CustomerTaskCalendarDashboardLogicProps {
    allRowData: TwinDictionary
}

const useCustomerTaskCalendarDashboardLogic = ({ allRowData }: CustomerTaskCalendarDashboardLogicProps) => {
    const { t } = useTwinTranslation()
    const [events, setEvents] = useState<TwinBigCalendarEvents | null>(null)
    const [year, setYear] = useState<number>(new Date().getFullYear())
    const taskWithNoName = t('taskWithNoName', 'Tarea sin nombre')

    const parseDataToEvents = useCallback((data: TaskModelType[]) => {
        const eventsParsed: TwinBigCalendarEvents = []
        let minHour = 8
        for (const task of data) {
            const startDate = task.start_date ? new Date(task.start_date) : undefined
            const endDate = task.end_date ? new Date(task.end_date) : undefined
            const taskTitle = task?.name || taskWithNoName
            let taskTitleComplete = `#${task.id} - ${task?.name || taskWithNoName}`
            if (task?.CustomerProjectId) {
                const codeCustomerToShow = getConfigParam('codeCustomerToShow')
                let fieldId: 'id' | 'code' = 'id'
                if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
                    fieldId = 'code'
                }
                const code = task?.CustomerProject?.Customer?.[fieldId] ? (task?.CustomerProject?.Customer?.[fieldId] + ' - ') : ''
                taskTitleComplete += `\n\n${code} ${task?.CustomerProject?.Customer?.name}`
            }
            if (task?.Employee?.name) {
                taskTitleComplete += `\n\n${task?.Employee?.fullname_short}`
            }
            if (task?.Group?.name) {
                taskTitleComplete += `\n\n${task.Group?.name}`
            }

            const color = (task?.TaskType?.color || '#43BAA5')
            eventsParsed.push({
                id: task.id,
                title: taskTitle,
                subtitle: taskTitleComplete,
                start: startDate || endDate,
                end: endDate || startDate,
                color,
                borderColor: color,
                textColor: '#515966'
            })
            
            if (startDate && startDate.getHours() < minHour) {
                minHour = startDate.getHours()
            }
        }

        setEvents(eventsParsed)
    }, [setEvents, taskWithNoName])

    const handleOnNavigate = useCallback((date: Date, view: string) => {
        if (view === 'year') {
            setYear(date.getFullYear())
        }
    }, [setYear])

    const getTaskCalendarData = useCallback(async () => {
        const dateStart = new Date(year, 0, 1)
        const dateEnd = new Date(year, 11, 31)
        setEvents(null)
        const result = await twinFetchPostJSON('/api/app/task/getAllTaskForCalendar', {
            start_date: dateStart,
            end_date: dateEnd,
            employees: [],
            groups: [],
            orderToParse: [['start_date', 'ASC']],
            whereToParse: {
                'CustomerProject.CustomerId': allRowData?.id,
            }
        })
        if (result) {
            parseDataToEvents(result.tasks)
        }
    }, [parseDataToEvents, year, setEvents, allRowData?.id])

    useEffect(() => {
        getTaskCalendarData()
    }, [getTaskCalendarData])

    useEffect(() => {
        listenSocket('getAllTasks', getTaskCalendarData, true)
        return () => {
            unlistenSocket('getAllTasks')
        }
    }, [getTaskCalendarData])

    return { events, getTaskCalendarData, setYear, year, handleOnNavigate }
}

export default CustomerTaskCalendarDashboard

